// コメント本文の入力欄（新規コメント・返信・編集で共用）。
// - ENTER で確定、SHIFT+ENTER で改行（FigJam の操作感に合わせる）。
// - Escape でキャンセル（onCancel）。
// - 入力に応じて高さを自動調整する（最大高さで内部スクロール）。
// - imageUploader が渡された場合のみ、クリップボードからの画像貼り付けを受け付ける
//   （新規コメント・返信で有効。編集は本文のみの変更のため渡さない）。
//   貼り付けた画像は即 pending へのアップロードを開始し、入力欄の上へサムネイルチップとして
//   表示する。公開領域への昇格（finalize）は送信確定時に行い、送信せずに取り消した場合は
//   pending の S3 Lifecycle（1 日）が自動回収する（孤児の公開添付を作らない）。
//   アップロード（PUT）完了前・昇格中は送信できない（未確定の URL を op に載せないため）。
// - リンク付き HTML の貼り付けは、表示テキストを保った [テキスト](URL) 形式へ変換して取り込む
//   （付箋・テキストの preserveHtmlAnchorsOnPaste と同じ仕様。表示側 CommentMessageBody →
//   LinkifiedCommentText が同記法をリンクとして描画する）。新規・返信・編集のすべてで有効。
//   画像とテキストが同時に載っている場合は従来どおり画像を優先する。
// 入力中の textarea はグローバルショートカット（削除 / Undo 等）の対象外として扱われる
// （use-global-shortcuts の isEditingTarget 判定）。キー操作はここで stopPropagation する。

import { ArrowUpwardIconData, CloseIconData } from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useShowToast } from "../notice/use-show-toast";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { COMMENT_BODY_MAX_LENGTH, COMMENT_IMAGES_MAX } from "@refinement-canvas/domain";
import { htmlFragmentToLinkText } from "../../../lib/paste-links";
import type { CommentImagePendingUpload, CommentImageUploader } from "./comment-image-upload";

interface Props {
  readonly placeholder: string;
  readonly initialValue?: string | undefined;
  /** 確定時に本文と添付画像 URL（アップロード済み公開 URL）を返す。画像なしのときは空配列。 */
  readonly onSubmit: (text: string, images: readonly string[]) => void;
  readonly onCancel?: (() => void) | undefined;
  readonly autoFocus?: boolean | undefined;
  /** 確定後に入力欄を空に戻すか（新規・返信=true、編集=false）。 */
  readonly clearOnSubmit?: boolean | undefined;
  readonly maxHeight?: number | undefined;
  /** 画像貼り付けを有効にする場合のアップローダ。未指定なら画像貼り付けは無効（編集用）。 */
  readonly imageUploader?: CommentImageUploader | undefined;
  /**
   * 送信確定処理（添付画像の昇格）中かどうかの変化を親へ通知する。
   * 親（入力ウィンドウ / ポップオーバー層）は、この間コメント UI を閉じない（アンマウントしない）
   * ようにして、送信の中断・確定済み URL キャッシュの喪失・公開添付の孤児化を防ぐ。
   */
  readonly onSubmittingChange?: ((submitting: boolean) => void) | undefined;
}

const MAX_TEXTAREA_HEIGHT = 160;

/** 添付中（アップロード中を含む）の画像チップ 1 件分の状態。 */
interface ComposerImage {
  /** チップ識別用のローカル ID。 */
  readonly id: string;
  /** 即時プレビュー用の object URL（破棄時に revoke する）。 */
  readonly previewUrl: string;
  /** pending への PUT が完了した実体の参照。null はアップロード（PUT）中。 */
  readonly pending: CommentImagePendingUpload | null;
  /**
   * finalize（公開領域への昇格）済みの公開 URL。null は未昇格。
   * 昇格は冪等でない（pending 実体は昇格時に削除される）ため、複数枚のうち一部だけ昇格に
   * 成功して送信が中止された場合の再送信で、昇格済みチップを再 finalize（404 で失敗）しない
   * よう、確定済み URL をチップへキャッシュして再利用する。
   */
  readonly url: string | null;
}

export function CommentComposer({
  placeholder,
  initialValue = "",
  onSubmit,
  onCancel,
  autoFocus = false,
  clearOnSubmit = true,
  maxHeight = MAX_TEXTAREA_HEIGHT,
  imageUploader,
  onSubmittingChange,
}: Props): JSX.Element {
  const [value, setValue] = useState(initialValue);
  const [images, setImages] = useState<readonly ComposerImage[]>([]);
  // 送信確定処理（添付画像の昇格 → onSubmit）の実行中フラグ。二重送信を防ぐ。
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const showToast = useShowToast();
  // アンマウント後の setState を避けつつ、破棄済みチップの object URL を確実に revoke する。
  const mountedRef = useRef(true);
  // チップのローカル ID 採番（アップロードの完了順が前後しても対象チップを特定できるようにする）。
  const nextImageIdRef = useRef(0);
  // アンマウント時の解放用に最新のチップ一覧を ref へ写す（アンマウント後の setState は React に
  // 無視され updater が走らないため、cleanup では state 経由でなく ref から revoke する）。
  const imagesRef = useRef<readonly ComposerImage[]>(images);
  imagesRef.current = images;

  // 初期値が変わったとき（別メッセージの編集に切り替わった等）に同期する。
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // アンマウント時に未送信チップのプレビュー URL を解放する。
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      for (const image of imagesRef.current) URL.revokeObjectURL(image.previewUrl);
    };
  }, []);

  // 送信確定処理中かどうかを親へ通知する（親は送信中このコンポーネントを閉じない）。
  // アンマウント時は必ず false を通知し、送信中フラグが親に残り続けて UI が閉じられなくなる
  // 事故を防ぐ。
  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);
  useEffect(() => {
    return () => onSubmittingChange?.(false);
  }, [onSubmittingChange]);

  // autoFocus 指定時はマウント直後に明示的にフォーカスを当てる。
  // HTML の autoFocus 属性は、配置クリックの pointerup などで他要素へフォーカスが移った後だと
  // 効かないことがあるため、ref 経由で確実にフォーカスし、キャレットを末尾へ置く。
  // 次フレームでも一度フォーカスを当て直し、フォーカス奪取に競り負けないようにする。
  useEffect(() => {
    if (!autoFocus) return undefined;
    const el = textareaRef.current;
    if (!el) return undefined;
    const focusToEnd = (): void => {
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    };
    focusToEnd();
    const raf = window.requestAnimationFrame(focusToEnd);
    return () => window.cancelAnimationFrame(raf);
  }, [autoFocus]);

  // 内容に合わせて高さを自動調整する。
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [value, maxHeight]);

  const trimmed = value.trim();
  const uploading = images.some((image) => image.pending === null);
  // 本文か画像のどちらかがあれば送信可（画像だけのコメントを許容）。
  // アップロード（PUT）完了待ち・送信確定処理中は不可。
  const canSubmit = (trimmed.length > 0 || images.length > 0) && !uploading && !submitting;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    // 空白のみの本文は空文字へ正規化する（画像だけの投稿で不可視の空白本文を保存しない）。
    const body = trimmed.length === 0 ? "" : value;
    const pendings = images
      .map((image) => image.pending)
      .filter((pending): pending is CommentImagePendingUpload => pending !== null);
    // 添付画像がある場合は、送信確定のこのタイミングで公開領域へ昇格して URL を確定する
    // （貼り付け時に昇格すると、送信せず取り消した画像が公開添付として孤児化するため）。
    // なお「全画像の昇格成功後にコメント op の送信・適用が失敗すると公開添付だけが残る」余地は、
    // 既存の画像オブジェクト（uploadAndPlaceImage: 昇格 → attachment.update op）と同じ非原子性で
    // あり、本機能でも同水準として許容する（昇格とコメント作成のサーバ側一括確定は将来課題）。
    // 送信確定後に外側クリック等で入力 UI が閉じられて（アンマウントされて）も、送信処理は
    // 中断せず onSubmit まで完遂する（Enter 済みの送信を落とさず、昇格済み画像の孤児化も防ぐ）。
    // このため以降の処理はアンマウント後も継続し、setState だけを mountedRef で抑止する。
    if (pendings.length > 0 && imageUploader) {
      setSubmitting(true);
      const urls: string[] = [];
      for (const chip of images) {
        if (chip.pending === null) continue;
        // 前回の送信試行で昇格済みのチップは、確定済み URL を再利用する（昇格は冪等でないため）。
        if (chip.url !== null) {
          urls.push(chip.url);
          continue;
        }
        const result = await imageUploader.finalize(chip.pending);
        if (!result.ok) {
          // 1 枚でも確定に失敗したら送信を中止する（チップは残るため、取り消すか再送信できる。
          // ここまでに昇格済みのチップは URL をキャッシュ済みで、再送信時に二重昇格しない）。
          if (mountedRef.current) setSubmitting(false);
          showToast("error", result.message);
          return;
        }
        urls.push(result.url);
        // 昇格に成功したチップへ確定 URL を記録する（以降の失敗→再送信で再利用するため）。
        if (mountedRef.current) {
          setImages((current) =>
            current.map((image) => (image.id === chip.id ? { ...image, url: result.url } : image))
          );
        }
      }
      if (mountedRef.current) setSubmitting(false);
      onSubmit(body, urls);
    } else {
      onSubmit(body, []);
    }
    if (clearOnSubmit && mountedRef.current) {
      setValue("");
      setImages((current) => {
        for (const image of current) URL.revokeObjectURL(image.previewUrl);
        return [];
      });
    }
  };

  // リンク付き HTML の貼り付け（画像を含まない場合）で、React の再レンダー後にキャレットを
  // 挿入テキストの末尾へ移すための保留位置。null は保留なし。
  const pendingCaretRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret === null) return;
    pendingCaretRef.current = null;
    const el = textareaRef.current;
    if (!el) return;
    const position = Math.min(caret, el.value.length);
    el.setSelectionRange(position, position);
  }, [value]);

  // クリップボードに画像ファイルが含まれていたらアップロードを開始し、チップとして表示する。
  // テキストと画像が同時に載っている場合は画像を優先する（FigJam と同様、ファイル名文字列の
  // 混入を防ぐため既定の貼り付けを抑止する）。
  // 画像が無い場合は、リンク付き HTML（<a href>）を表示テキストを保った [テキスト](URL) 形式へ
  // 変換して挿入する（付箋・テキストの貼り付けと同じ仕様）。アンカーが無ければ何もせず、
  // ブラウザ既定の貼り付け（text/plain）へ委ねる。
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
    // 送信確定処理中の貼り付けは画像チップを追加しない（clearOnSubmit のクリアで、送信対象外の
    // 新チップが投稿されないまま消えてしまう競合を避ける）。リンク変換も行わず、テキストは
    // ブラウザ既定の貼り付けに任せる（送信中の手入力と同じ扱い）。
    if (submitting) return;
    const files = Array.from(event.clipboardData?.items ?? [])
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    if (!imageUploader || files.length === 0) {
      // 画像を処理しない経路（画像なし、または画像貼り付け無効の編集時）: リンク保持のテキスト変換を試す。
      const converted = htmlFragmentToLinkText(event.clipboardData?.getData("text/html") ?? "");
      if (converted === undefined) return;
      event.preventDefault();
      const el = event.currentTarget;
      // 対応環境では execCommand("insertText") で挿入する（use-object-text-editor の非 markdown
      // 経路と同方針）。ブラウザの編集パイプラインを通るため、ネイティブ undo スタック・
      // maxLength・キャレット位置が通常の貼り付けと同じに保たれ、input イベント経由で
      // onChange → value state へ反映される。
      if (
        typeof document.execCommand === "function" &&
        document.execCommand("insertText", false, converted)
      ) {
        return;
      }
      // 非対応環境のフォールバック: 手動挿入。textarea の maxLength は手動挿入（setValue）では
      // 効かないため、ブラウザ既定の maxLength 貼り付けと同様に「挿入分だけ」を空き容量まで
      // クリップし、キャレット前後の既存本文は保持する（全体の末尾切り詰めでキャレット以降の
      // 既存テキストを黙って失わない）。
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const available = Math.max(0, COMMENT_BODY_MAX_LENGTH - (value.length - (end - start)));
      let clipped = converted.slice(0, available);
      // UTF-16 コードユニット境界の機械的な切り詰めがサロゲートペア（絵文字等）を分断して
      // 不正な孤立サロゲートを残さないよう、末尾が上位サロゲートなら 1 つ手前で切る。
      const lastCode = clipped.charCodeAt(clipped.length - 1);
      if (lastCode >= 0xd800 && lastCode <= 0xdbff) clipped = clipped.slice(0, -1);
      const next = value.slice(0, start) + clipped + value.slice(end);
      if (next === value) {
        // 値が変わらない（上限到達で挿入分が全て切り落とされた等）場合は、同値 setState の
        // ベイルアウトで layout effect が走らないため、保留キャレットを残さない
        // （残すと後続編集時に古い位置へキャレットが飛ぶ）。
        return;
      }
      pendingCaretRef.current = start + clipped.length;
      setValue(next);
      return;
    }
    event.preventDefault();
    const remaining = COMMENT_IMAGES_MAX - images.length;
    if (remaining <= 0) {
      showToast(
        "warning",
        `1 件のコメントに添付できる画像は ${COMMENT_IMAGES_MAX} 枚までです。`
      );
      return;
    }
    if (files.length > remaining) {
      showToast(
        "warning",
        `1 件のコメントに添付できる画像は ${COMMENT_IMAGES_MAX} 枚までです。超過分は追加されません。`
      );
    }
    for (const file of files.slice(0, remaining)) {
      const id = `img-${nextImageIdRef.current++}`;
      const previewUrl = URL.createObjectURL(file);
      setImages((current) => [...current, { id, previewUrl, pending: null, url: null }]);
      void imageUploader.upload(file).then((result) => {
        if (!mountedRef.current) {
          // アンマウント済みなら状態は触らない（プレビュー URL はアンマウント時 effect で解放済み。
          // PUT 済みの pending 実体は S3 Lifecycle が自動回収する）。
          return;
        }
        if (result.ok) {
          setImages((current) =>
            current.map((image) =>
              image.id === id ? { ...image, pending: result.pending } : image
            )
          );
        } else {
          // 失敗したチップは取り除き、原因の分かる文言で通知する。
          setImages((current) => {
            const target = current.find((image) => image.id === id);
            if (target) URL.revokeObjectURL(target.previewUrl);
            return current.filter((image) => image.id !== id);
          });
          showToast("error", result.message);
        }
      });
    }
  };

  const removeImage = (id: string): void => {
    // 送信確定処理中は取り消し不可（finalize 済み・送信予定のチップ集合を変えない）。
    if (submitting) return;
    setImages((current) => {
      const target = current.find((image) => image.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((image) => image.id !== id);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // textarea 内のキー操作はキャンバスのショートカットへ伝播させない。
    event.stopPropagation();
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      // IME 変換確定の ENTER（isComposing）では送信しない。
      event.preventDefault();
      void submit();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      // 送信確定処理（画像の昇格）中の Escape は無視する（閉じても送信自体は完遂されるが、
      // 進行中であることが分かるよう UI は開いたままにする）。
      if (submitting) return;
      onCancel?.();
    }
  };

  const sendButtonStyle: CSSProperties = {
    flex: "0 0 auto",
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    border: "none",
    display: "grid",
    placeItems: "center",
    cursor: canSubmit ? "pointer" : "default",
    background: canSubmit ? "var(--color-accent, #7c4dff)" : "var(--color-border, #d4d4dd)",
    color: "#ffffff",
    transition: "background 0.15s ease",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
      {images.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {images.map((image) => (
            <div
              key={image.id}
              data-testid="comment-composer-image-chip"
              style={{ position: "relative", width: "48px", height: "48px" }}
            >
              <img
                src={image.previewUrl}
                alt="添付画像のプレビュー"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid var(--color-border, #d4d4dd)",
                  // アップロード中は半透明にして未確定であることを示す。
                  opacity: image.pending === null ? 0.5 : 1,
                }}
              />
              {image.pending === null ? (
                <span
                  aria-label="画像をアップロード中"
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    fontSize: "16px",
                    color: "var(--color-text-muted, #6b6f7d)",
                    pointerEvents: "none",
                  }}
                >
                  …
                </span>
              ) : null}
              <button
                type="button"
                aria-label="添付画像を取り消す"
                title="添付画像を取り消す"
                disabled={submitting}
                onClick={() => removeImage(image.id)}
                onPointerDown={(event) => event.stopPropagation()}
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  border: "none",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  background: "var(--color-text, #1a1a2e)",
                  color: "#ffffff",
                  fontSize: "10px",
                  padding: 0,
                }}
              >
                <Icon d={CloseIconData} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "8px",
          width: "100%",
        }}
      >
        <textarea
          ref={textareaRef}
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          rows={1}
          maxLength={COMMENT_BODY_MAX_LENGTH}
          aria-label={placeholder}
          onChange={(event) => setValue(event.currentTarget.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          // クリックがキャンバスへ抜けないようにする（オーバーレイ内のフォーム要素として完結させる）。
          onPointerDown={(event) => event.stopPropagation()}
          style={{
            flex: "1 1 auto",
            resize: "none",
            border: "none",
            outline: "none",
            background: "transparent",
            font: "inherit",
            fontSize: "14px",
            lineHeight: 1.5,
            color: "var(--color-text, #1a1a2e)",
            padding: "4px 0",
            maxHeight: `${maxHeight}px`,
            overflowY: "auto",
          }}
        />
        <button
          type="button"
          aria-label="送信"
          title={submitting ? "送信中…" : "送信（Enter）"}
          disabled={!canSubmit}
          onClick={() => void submit()}
          onPointerDown={(event) => event.stopPropagation()}
          style={sendButtonStyle}
        >
          <Icon d={ArrowUpwardIconData} />
        </button>
      </div>
    </div>
  );
}
